/**
 * Period keys & date ranges for week / month / quarter / year closings.
 * Local calendar (same convention as dailyClosingService).
 */

export const PERIOD_TYPES = Object.freeze({
  WEEK: 'week',
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year'
});

/** Periods that block sync until manager review. */
export const REVIEW_BLOCKING_PERIOD_TYPES = Object.freeze([
  PERIOD_TYPES.MONTH,
  PERIOD_TYPES.QUARTER,
  PERIOD_TYPES.YEAR
]);

function pad2(n) {
  return String(n).padStart(2, '0');
}

export function toLocalDateString(referenceDate) {
  const d = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** ISO week number (1–53) for a local date. */
export function getIsoWeekParts(referenceDate = new Date()) {
  const d = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate()
  );
  // Thursday in current week decides the year
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const weekYear = d.getFullYear();
  const week1 = new Date(weekYear, 0, 4);
  const week =
    1 +
    Math.round(
      ((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7
    );
  return { weekYear, week };
}

export function formatWeekKey(weekYear, week) {
  return `${weekYear}-W${pad2(week)}`;
}

export function formatMonthKey(year, monthIndex0) {
  return `${year}-${pad2(monthIndex0 + 1)}`;
}

export function formatQuarterKey(year, quarter) {
  return `${year}-Q${quarter}`;
}

export function formatYearKey(year) {
  return String(year);
}

/** Monday 00:00 local of the ISO week containing `referenceDate`. */
export function getIsoWeekRange(referenceDate = new Date()) {
  const d = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate()
  );
  const day = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  const { weekYear, week } = getIsoWeekParts(start);
  return {
    type: PERIOD_TYPES.WEEK,
    periodKey: formatWeekKey(weekYear, week),
    startDate: toLocalDateString(start),
    endDate: toLocalDateString(end),
    start,
    end
  };
}

export function getMonthRange(year, monthIndex0) {
  const start = new Date(year, monthIndex0, 1, 0, 0, 0, 0);
  const end = new Date(year, monthIndex0 + 1, 0, 23, 59, 59, 999);
  return {
    type: PERIOD_TYPES.MONTH,
    periodKey: formatMonthKey(year, monthIndex0),
    startDate: toLocalDateString(start),
    endDate: toLocalDateString(end),
    start,
    end
  };
}

export function getQuarterRange(year, quarter) {
  const q = Math.min(4, Math.max(1, Number(quarter) || 1));
  const startMonth = (q - 1) * 3;
  const start = new Date(year, startMonth, 1, 0, 0, 0, 0);
  const end = new Date(year, startMonth + 3, 0, 23, 59, 59, 999);
  return {
    type: PERIOD_TYPES.QUARTER,
    periodKey: formatQuarterKey(year, q),
    startDate: toLocalDateString(start),
    endDate: toLocalDateString(end),
    start,
    end
  };
}

export function getYearRange(year) {
  const start = new Date(year, 0, 1, 0, 0, 0, 0);
  const end = new Date(year, 11, 31, 23, 59, 59, 999);
  return {
    type: PERIOD_TYPES.YEAR,
    periodKey: formatYearKey(year),
    startDate: toLocalDateString(start),
    endDate: toLocalDateString(end),
    start,
    end
  };
}

/**
 * Periods that are fully elapsed as of `now` and may be closed once.
 * Order: week → month → quarter → year (dependencies first).
 */
export function getDuePeriods(now = new Date()) {
  const due = [];
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Previous ISO week (complete when today is after that week's Sunday)
  const thisWeek = getIsoWeekRange(today);
  const prevWeekEnd = new Date(thisWeek.start);
  prevWeekEnd.setDate(prevWeekEnd.getDate() - 1);
  if (prevWeekEnd < today) {
    due.push(getIsoWeekRange(prevWeekEnd));
  }

  // Previous calendar month
  const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  due.push(getMonthRange(prevMonth.getFullYear(), prevMonth.getMonth()));

  // Previous quarter (due from the 1st day of a new quarter)
  const currentQuarter = Math.floor(today.getMonth() / 3) + 1;
  let prevQ = currentQuarter - 1;
  let prevQYear = today.getFullYear();
  if (prevQ < 1) {
    prevQ = 4;
    prevQYear -= 1;
  }
  // Only due once the previous quarter has fully ended
  const prevQRange = getQuarterRange(prevQYear, prevQ);
  if (today > prevQRange.end) {
    due.push(prevQRange);
  }

  // Previous year (due from Jan 1)
  const prevYear = today.getFullYear() - 1;
  const prevYearRange = getYearRange(prevYear);
  if (today > prevYearRange.end) {
    due.push(prevYearRange);
  }

  return due;
}

export function dateKeyInRange(dateKey, startDate, endDate) {
  if (!dateKey) return false;
  const key = String(dateKey).slice(0, 10);
  return key >= startDate && key <= endDate;
}

export function monthsInQuarter(year, quarter) {
  const q = Math.min(4, Math.max(1, Number(quarter) || 1));
  const startMonth = (q - 1) * 3;
  return [0, 1, 2].map((offset) => formatMonthKey(year, startMonth + offset));
}

export function quartersInYear(year) {
  return [1, 2, 3, 4].map((q) => formatQuarterKey(year, q));
}
